import React, { FC, ReactElement, useRef, useState } from 'react';
import cns from 'classnames';
import { useSelector } from 'react-redux';
import { internalizationState } from '../../../../stores/reducers/sections';
import localeFieldResolver from '../../../../helpers/locale-field-finder';
import { getHomePageQuizList, IQuizItem, IQuizItemPopup } from '../../../../stores/reducers/quiz';
import Swiper from 'react-id-swiper';
import { goNext, goPrev } from '../../../../helpers/swiper';
import Icon from '../../../Icon';
import QuizModal from './QuizModal';
import { getIsLoggedIn } from '../../../../stores/reducers/userData';

import './quiz.sass';

interface Props {
  className?: string | Array<string>;
  bgSettings?: {
    color?: string;
    isDark?: boolean;
  };
  sectionName?: string;
  sectionId: string;
  sectionData?: any;
}

export interface IQuizModalData {
  isOpen: boolean;
  close: () => void;
  quiz: IQuizItem | IQuizItemPopup | null;
  styles?: {
    backgroundColor?: string;
    activeColor?: string;
    questionColor?: string;
    buttonColor?: string;
  };
}

const swiperParams = {
  watchOverflow: true,
  slidesPerView: 3,
  spaceBetween: 40,
  loop: false,
};

const Quiz: FC<Props> = ({ className, bgSettings, sectionName, sectionId, sectionData }): ReactElement => {
  const { activeLangState } = useSelector(internalizationState);
  const quizList = useSelector(getHomePageQuizList);
  const isUserAuth = useSelector(getIsLoggedIn);
  const swiperRef = useRef(null);

  const quizButtonColor = sectionData.params.find((param: {type: string}) => param.type === 'quizButtonColor').value;
  const quizFontSize = sectionData.params.find((param: {type: string}) => param.type === 'quizFontSize').value;
  const quizPopupBackground = sectionData.params.find((param: {type: string}) => param.type === 'quizPopupBackground').value;
  const quizAnswerBackground = sectionData.params.find((param: {type: string}) => param.type === 'quizAnswerBackground').value;
  const quizFontColor = sectionData.params.find((param: {type: string}) => param.type === 'quizFontColor').value;
  const quizModalFontColor = sectionData.params.find((param: {type: string}) => param.type === 'quizModalFontColor').value;
  const quizModalButtonColor = sectionData.params.find((param: {type: string}) => param.type === 'quizModalButtonColor').value;

  const [quizModalData, setQuizModalData] = useState<IQuizModalData>({
    isOpen: false,
    close: () => setQuizModalData({ ...quizModalData, isOpen: false }),
    quiz: null,
  });

  const updateQuizModalData = (quiz: IQuizItem) => {
    setQuizModalData({...quizModalData, isOpen: true, quiz});
  };

  const quizItemStyleParams = (quiz: IQuizItem) => {
    let styleParams = {
      backgroundImage: '',
      backgroundColor: quiz.fillColor,
    };

    if (quiz.image) {
      styleParams.backgroundImage = `url(${quiz.image})`
    }

    return styleParams;
  };

  const filteredQuizList = quizList.filter((quiz: IQuizItem) => {
    if (quiz.access === 0) {
      if (isUserAuth) {
        return quiz;
      }
      return null;
    }
      return quiz;
  });

  const quizTemplate = (quiz: IQuizItem) => (
    <div
      key={quiz.id}
      className="swiper-slide quiz__slider__item"
      style={quizItemStyleParams(quiz)}
    >
      <p
        className="quiz__slider__question"
        style={{
          fontSize: parseInt(quizFontSize),
          color: quizFontColor,
        }}
      >
        {localeFieldResolver(quiz, 'question', activeLangState)}
      </p>
      {
        quiz.hasAnswers === false &&
        (
          <button
            className="quiz__slider__button"
            style={{
              backgroundColor: quizButtonColor,
            }}
            onClick={() => updateQuizModalData(quiz)}
          >
            {activeLangState === 'ru' ? 'Ответить' : 'Answer'}
          </button>
        )
      }
    </div>
  );
  

  return (
    filteredQuizList.length ?
      <section
        className={cns('inner-section', bgSettings && bgSettings.isDark && 'inner-section--dark-bg', className)}
        style={{ backgroundColor: bgSettings && bgSettings.color }}
      >
        <div className="container" id={sectionId}>
          <div className="quiz-inner">
            <div className="section-title">{sectionName}</div>
            <div className="quiz__slider-container">
              <Swiper ref={swiperRef} {...swiperParams}>
                {
                  filteredQuizList.map((quiz) => (quizTemplate(quiz)))
                }
              </Swiper>
              {filteredQuizList.length > 3 && (
                <div className="slider-nav hidden-mobile">
                  <button className="slider-nav__prev" type="button" onClick={() => goPrev(swiperRef)}>
                    <Icon name="md-arr-left" />
                  </button>
                  <button className="slider-nav__next" type="button" onClick={() => goNext(swiperRef)}>
                    <Icon name="md-arr-right" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <QuizModal
          isOpen={quizModalData.isOpen}
          close={quizModalData.close}
          quiz={quizModalData.quiz}
          styles={{
            backgroundColor: quizPopupBackground,
            activeColor: quizAnswerBackground,
            questionColor: quizModalFontColor,
            buttonColor: quizModalButtonColor,
          }}
        />
      </section>
    :
      <></>
  );
};

export default Quiz;
